import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ToastProvider } from "@/components/ToastProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import AdminGuard from "@/components/admin/AdminGuard";
import RoleGuard from "@/components/admin/RoleGuard";
import { defaultPathForRole } from "@/lib/rbac";
import { useAdminAuthStore } from "@/store/admin-auth-store";
import { Loader2 } from "lucide-react";

const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));
const LoginPage = lazy(() => import("@/app/login/page"));
const DashboardPage = lazy(() => import("@/app/dashboard/page"));
const UsersPage = lazy(() => import("@/app/users/page"));
const UserDetailPage = lazy(() => import("@/app/users/detail"));
const CustomerServicePage = lazy(() => import("@/app/customer-service/page"));
const ActivationLookupPage = lazy(() => import("@/app/customer-service/activation-lookup"));
const AdvisorNumberDetailPage = lazy(() => import("@/app/customer-service/advisor-detail"));
const WhatsAppSettingsPage = lazy(() => import("@/app/customer-service/settings"));
const CreditsSettingsPage = lazy(() => import("@/app/settings/credits/page"));
const CreditPacksPage = lazy(() => import("@/app/settings/credit-packs/page"));
const PaymentProvidersPage = lazy(() => import("@/app/settings/payment-providers/page"));
const PaymentEventsPage = lazy(() => import("@/app/settings/payment-events/page"));
const RevenuePage = lazy(() => import("@/app/revenue/page"));
const ReferralsPage = lazy(() => import("@/app/referrals/page"));
const PartnerDocsPage = lazy(() => import("@/app/partner-docs/page"));
// const CommunityPage = lazy(() => import("@/app/community/page")); // HIDDEN: no BE API (mock)
const AdvisorPage = lazy(() => import("@/app/advisor/page"));
const AdvisorMatchesPage = lazy(() => import("@/app/advisor/matches/page"));
// Credit-only billing: Revenue / Plans / Pricing / Subscriptions pages removed
// (subscription backend gỡ). Credit Packs (settings/credit-packs) thay thế.
const TradingPage = lazy(() => import("@/app/trading/page"));
// const PoolPage = lazy(() => import("@/app/pool/page")); // HIDDEN: no BE API (mock)
const DatasourcesPage = lazy(() => import("@/app/datasources/page"));
// const RegistryPage = lazy(() => import("@/app/registry/page")); // HIDDEN: no BE (/api/config/registry 404)
// ── HIDDEN: pages with no BE API yet (mock/empty data) — re-enable when their
//    /fx7-ctrl/* endpoints ship. See ADVISOR-VIP-CONCIERGE-PLAN audit.
// const ApiOpsPage = lazy(() => import("@/app/api-ops/page"));
// const AiPage = lazy(() => import("@/app/ai/page"));
// const MonitorPage = lazy(() => import("@/app/monitor/page"));
// const SystemPage = lazy(() => import("@/app/system/page"));
const SystemAdminsPage = lazy(() => import("@/app/system/admins/page"));
const SystemRolesPage = lazy(() => import("@/app/system/roles/page"));
const SystemTopologyPage = lazy(() => import("@/app/system/topology/page"));
const FeatureFlagsPage = lazy(() => import("@/app/system/feature-flags/page"));
const RuntimeConfigPage = lazy(() => import("@/app/system/runtime-config/page"));
const IntegrationsPage = lazy(() => import("@/app/system/integrations/page"));
const AIQualityPage = lazy(() => import("@/app/ai-quality/page"));
const AIProvidersPage = lazy(() => import("@/app/ai-providers/page"));
const AIModelsPage = lazy(() => import("@/app/ai-models/page"));
const AIRoutingPage = lazy(() => import("@/app/ai-routing/page"));
const AIRunsPage = lazy(() => import("@/app/ai-runs/page"));
const AIEvalPage = lazy(() => import("@/app/ai-eval/page"));
const AIToolsPage = lazy(() => import("@/app/ai-tools/page"));
const AIWorkflowsPage = lazy(() => import("@/app/ai-workflows/page"));
const AICatalogPage = lazy(() => import("@/app/ai-catalog/page"));
const AIAgentsPage = lazy(() => import("@/app/ai-agents/page"));
const AISkillsPage = lazy(() => import("@/app/ai-skills/page"));
const AISettingsPage = lazy(() => import("@/app/ai-settings/page"));
const AuditPage = lazy(() => import("@/app/audit/page"));
const AnnouncementsPage = lazy(() => import("@/app/announcements/page"));
const CampaignsPage = lazy(() => import("@/app/campaigns/page"));
const AdvisorCapacityPage = lazy(() => import("@/app/advisor/capacity/page"));
const AdvisorAllConversationsPage = lazy(
  () => import("@/app/advisor/conversations/page"),
);
const AdvisorSettingsPage = lazy(() => import("@/app/advisor/settings/page"));
// const AdvisorQuotaPage = lazy(() => import("@/app/advisor/quota/page")); // HIDDEN: no BE (/fx7-ctrl/advisor/quota-usage 404)
const AdvisorAgentsPage = lazy(() => import("@/app/advisor/agents/page"));
const AdvisorRecommendationsPage = lazy(() => import("@/app/advisor/recommendations/page"));
const AdvisorOutreachPage = lazy(() => import("@/app/advisor/outreach/page"));
const AdvisorQueuePage = lazy(() => import("@/app/advisor/queue/page"));
const AdvisorSLAPage = lazy(() => import("@/app/advisor/sla/page"));
// const AdvisorSegmentsPage = lazy(() => import("@/app/advisor/segments/page")); // HIDDEN: no BE (/fx7-ctrl/advisor/segments 404)

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-s0">
      <Loader2 className="h-6 w-6 animate-spin text-t2" />
    </div>
  );
}

function IndexRedirect() {
  const role = useAdminAuthStore((s) => s.admin?.role ?? null);
  return <Navigate to={defaultPathForRole(role)} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <AdminGuard>
                  <AdminLayout />
                </AdminGuard>
              }
            >
              <Route index element={<IndexRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="ai-quality" element={<AIQualityPage />} />
              <Route path="ai-providers" element={<AIProvidersPage />} />
              <Route path="ai-models" element={<AIModelsPage />} />
              <Route path="ai-routing" element={<AIRoutingPage />} />
              <Route path="ai-runs" element={<AIRunsPage />} />
              <Route path="ai-eval" element={<AIEvalPage />} />
              <Route path="ai-tools" element={<AIToolsPage />} />
              <Route path="ai-workflows" element={<AIWorkflowsPage />} />
              <Route path="ai-catalog" element={<AICatalogPage />} />
              <Route path="ai-agents" element={<AIAgentsPage />} />
              <Route path="ai-skills" element={<AISkillsPage />} />
              <Route path="ai-settings" element={<AISettingsPage />} />
              <Route path="customer-service" element={<CustomerServicePage />} />
              <Route path="customer-service/activation-codes" element={<ActivationLookupPage />} />
              <Route path="customer-service/users" element={<CustomerServicePage />} />
              <Route path="customer-service/gift-codes" element={<CustomerServicePage />} />
              <Route path="customer-service/advisor/:id" element={<AdvisorNumberDetailPage />} />
              <Route path="customer-service/settings" element={<WhatsAppSettingsPage />} />
              <Route path="settings/credits" element={<CreditsSettingsPage />} />
              <Route path="settings/credit-packs" element={<CreditPacksPage />} />
              <Route path="settings/payment-providers" element={<PaymentProvidersPage />} />
              <Route path="settings/payment-events" element={<PaymentEventsPage />} />
              {/* HIDDEN (no BE API / mock): community */}
              <Route path="advisor" element={<AdvisorPage />} />
              <Route path="advisor/matches" element={<AdvisorMatchesPage />} />
              <Route path="advisor/capacity" element={<AdvisorCapacityPage />} />
              <Route
                path="advisor/conversations"
                element={<AdvisorAllConversationsPage />}
              />
              <Route
                path="advisor/settings"
                element={
                  <RoleGuard minRole="support_lead">
                    <AdvisorSettingsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="advisor/agents"
                element={
                  <RoleGuard minRole="support_lead">
                    <AdvisorAgentsPage />
                  </RoleGuard>
                }
              />
              {/* HIDDEN (no BE: quota-usage 404): advisor/quota */}
              <Route path="advisor/recommendations" element={<AdvisorRecommendationsPage />} />
              <Route
                path="advisor/outreach"
                element={
                  <RoleGuard minRole="support_lead">
                    <AdvisorOutreachPage />
                  </RoleGuard>
                }
              />
              <Route path="advisor/queue" element={<AdvisorQueuePage />} />
              <Route path="advisor/sla" element={<AdvisorSLAPage />} />
              {/* HIDDEN: no BE (/fx7-ctrl/advisor/segments 404)
              <Route
                path="advisor/segments"
                element={
                  <RoleGuard minRole="support_lead">
                    <AdvisorSegmentsPage />
                  </RoleGuard>
                }
              /> */}
              {/* Credit-only: revenue = credit top-up dashboard (plans/subs removed). */}
              <Route path="revenue" element={<RevenuePage />} />
              {/* Refer & earn payout ledger + clawback (mig 0164). */}
              <Route path="referrals" element={<ReferralsPage />} />
              <Route path="partner-docs" element={<PartnerDocsPage />} />
              <Route path="trading" element={<TradingPage />} />
              {/* HIDDEN (no BE API / mock): pool */}
              <Route path="datasources" element={<DatasourcesPage />} />
              {/* HIDDEN (no BE: /api/config/registry 404): registry */}
              {/* HIDDEN (no BE API / mock): api-ops, ai, monitor, system(info).
                  system/admins below stays — it has a real BE. */}
              <Route
                path="system/admins"
                element={
                  <RoleGuard minRole="super_admin">
                    <SystemAdminsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="system/roles"
                element={
                  <RoleGuard minRole="super_admin">
                    <SystemRolesPage />
                  </RoleGuard>
                }
              />
              {/* Topology map exposes service URLs + host metrics → gate on
                  nav.system (ops + super_admin). Plan §8 says minRole="admin";
                  this codebase has no "admin" role — nav.system is the
                  equivalent allowlist (see docs/SYSTEM-TOPOLOGY-DASHBOARD-PLAN.md §5). */}
              <Route
                path="system/topology"
                element={
                  <RoleGuard action="nav.system">
                    <SystemTopologyPage />
                  </RoleGuard>
                }
              />
              {/* Client feature flags — toggle large web-client sections live.
                  Read super_admin + ops (BE write is super_admin-only; the page
                  hides the toggles for non-super_admin). */}
              <Route
                path="system/feature-flags"
                element={
                  <RoleGuard action="nav.featureFlags">
                    <FeatureFlagsPage />
                  </RoleGuard>
                }
              />
              {/* Runtime Config — BE business/AI knobs (mig 0137), DB override
                  over yaml. Read super_admin + ops; BE write is super_admin. */}
              <Route
                path="system/runtime-config"
                element={
                  <RoleGuard action="nav.system">
                    <RuntimeConfigPage />
                  </RoleGuard>
                }
              />
              <Route
                path="system/integrations"
                element={
                  <RoleGuard action="nav.integrations">
                    <IntegrationsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="audit"
                element={
                  <RoleGuard minRole="super_admin">
                    <AuditPage />
                  </RoleGuard>
                }
              />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        <ToastProvider />
      </ErrorBoundary>
    </ThemeProvider>
  );
}
